'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Design-system Button — Lori DS spec node 32:493.
 *
 * Token notes:
 * - Primary scale tokens are hex (var(--primary-*)). Tailwind opacity modifiers
 *   (bg-primary-100/50) will NOT produce translucent colors — the button spec
 *   uses opaque fills only. This is intentional.
 * - Tailwind's built-in neutral scale (50–950) coexists with the DS neutral
 *   tokens (0/10/40/60/80/100). bg-neutral-0 → #fff (DS); bg-neutral-100 → DS
 *   ink #1f1e1c, which shadows Tailwind's light gray (see tailwind.config.ts).
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full font-sans font-semibold transition-all duration-150 motion-reduce:transition-none focus-visible:outline-none disabled:pointer-events-none [&_svg]:shrink-0 [&_svg]:pointer-events-none',
  {
    variants: {
      variant: {
        primary:
          'bg-primary-100 text-neutral-0 hover:bg-primary-60 active:shadow-button-pressed focus-visible:shadow-button-focus-primary disabled:bg-text-disabled disabled:text-text-non-essential',
        secondary:
          'bg-transparent text-primary-100 border border-primary-60 hover:bg-primary-40 active:bg-primary-40 active:shadow-button-pressed focus-visible:shadow-button-focus-secondary disabled:border-text-disabled disabled:text-text-non-essential',
        text: 'bg-transparent text-primary-100 hover:underline underline-offset-4 active:no-underline focus-visible:shadow-button-focus-secondary disabled:text-text-non-essential disabled:no-underline',
      },
      size: {
        sm: 'h-8 px-3 py-1.5 text-sm gap-1.5',
        md: 'h-10 px-4 py-2 text-base gap-2',
        lg: 'h-12 px-5 py-3 text-lg gap-2',
      },
    },
    compoundVariants: [
      { variant: 'text', size: 'sm', class: 'h-auto p-0 text-sm' },
      { variant: 'text', size: 'md', class: 'h-auto p-0 text-base' },
      // Compound variant prevents the h-12 lg sizing from applying;
      // dev warning below signals this combination is spec-unsupported.
      { variant: 'text', size: 'lg', class: 'h-auto p-0 text-lg' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    iconRight?: React.ReactNode;
    asChild?: boolean;
  };

function Button({
  className,
  variant,
  size,
  type,
  asChild = false,
  iconRight,
  children,
  ref,
  ...props
}: ButtonProps & { ref?: React.Ref<HTMLButtonElement> | undefined }) {
  // variant="text" + size="lg" is unsupported per Lori DS spec (node 32:493).
  // The previous dev-mode `console.warn` here has been replaced by the
  // `local/ban-button-text-lg` ESLint rule (#1670), which catches the combo
  // at lint time across all call sites including aliased imports. The
  // compound variant above (`{ variant: 'text', size: 'lg' } → h-auto p-0 text-lg`)
  // is the residual style-level safeguard for any caller that bypasses lint
  // via dynamic props.
  const Comp = asChild ? Slot : 'button';
  // type only applies to <button>; Slot ignores unknown attrs but spreading it onto an
  // <a> child would set a non-standard "type" attribute. Restrict it to the button path.
  const buttonOnlyProps = asChild ? {} : { type: type ?? 'button' };
  // When asChild, Slot requires exactly one React element child — wrap in a fragment
  // only for the native button path where iconRight may add a second node.
  const content = asChild ? (
    children
  ) : (
    <>
      {children}
      {iconRight}
    </>
  );
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      ref={ref}
      {...buttonOnlyProps}
      {...props}
    >
      {content}
    </Comp>
  );
}

export { Button };
